import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Render Fathom's `default_summary.markdown_formatted` (or any other
 * markdown-shaped summary) with project design tokens.
 *
 * Why a custom component + explicit overrides rather than a global
 * prose class: the Reverra dashboard uses a constrained type scale
 * (text-sm body, [11px] meta, lucide icons) — Tailwind Typography's
 * defaults would dominate the surrounding visual rhythm. Overriding
 * the elements we actually expect from Fathom (h2/h3, p, ul/ol, li,
 * strong, em, a) keeps the markdown tightly inside the meeting card.
 *
 * Untrusted-input safety: the markdown text originates from Fathom's
 * AI-generated summary — adversarial markdown can't execute scripts
 * (react-markdown defaults to safe rendering — no raw HTML, no inline
 * scripts). Links are rendered with target=_blank + rel=noreferrer
 * to defang any window.opener access.
 *
 * This component is intentionally self-contained — no app-level
 * imports (auth, supabase, react-query) — so tests can render it
 * without bootstrapping the rest of the app.
 */
export function MeetingSummaryMarkdown({ markdown }: { markdown: string }) {
  return (
    // `break-words` (overflow-wrap: break-word) handles long Fathom link
    // titles or URL-as-text that would otherwise blow past the card.
    // `min-w-0` lets this div shrink when nested in flex/grid parents.
    <div className="text-sm text-foreground leading-relaxed break-words min-w-0">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Heading level is preserved (## → <h2>, ### → <h3>) — the
          // override only restyles, never demotes. Demoting confuses
          // screen readers + assistive tech that rely on heading
          // semantics; we style each level distinctly inside the card.
          h1: ({ children }) => (
            <h2 className="text-base font-semibold text-foreground mt-3 mb-2 first:mt-0">
              {children}
            </h2>
          ),
          h2: ({ children }) => (
            <h2 className="text-base font-semibold text-foreground mt-3 mb-2 first:mt-0">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-sm font-semibold text-foreground mt-3 mb-1.5 first:mt-0">
              {children}
            </h3>
          ),
          h4: ({ children }) => (
            <h4 className="text-sm font-medium text-foreground mt-2 mb-1 first:mt-0">{children}</h4>
          ),
          p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
          ul: ({ children }) => (
            <ul className="list-disc pl-5 mb-2 space-y-1 marker:text-muted-foreground">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal pl-5 mb-2 space-y-1 marker:text-muted-foreground">
              {children}
            </ol>
          ),
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
          strong: ({ children }) => (
            <strong className="font-semibold text-foreground">{children}</strong>
          ),
          em: ({ children }) => <em className="italic">{children}</em>,
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noreferrer noopener"
              // `break-words` (overflow-wrap: break-word) keeps prose-like
              // link text wrapping on word boundaries but still breaks
              // pathologically long URL-as-text inside the link.
              className="text-forest underline decoration-forest/40 underline-offset-2 hover:decoration-forest break-words"
            >
              {children}
            </a>
          ),
          code: ({ children }) => (
            <code className="rounded bg-secondary px-1 py-0.5 text-[12px] font-mono">
              {children}
            </code>
          ),
          hr: () => <hr className="my-3 border-border" />,
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-border pl-3 italic text-muted-foreground">
              {children}
            </blockquote>
          ),
        }}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
}
